import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NODE_HUE, nodeTypeSurface } from '@/lib/node-visuals';
import { isImpossibleBlocker } from '@/lib/assumption-risk';
import type { NodeMetadata } from '@/services/canvas-mappers';
import type { Node as ReactFlowNode } from '@xyflow/react';

// One assumption as a title-only sticky. Everything else about it lives behind a
// click (the detail panel) — the board's job is showing where an assumption sits
// relative to the others, so a wall of prose would defeat it.
export function AssumptionSticky({
  node,
  onOpen,
  draggable,
  onDragStart,
  onDragEnd,
  dimmed,
  blocker,
  ringed,
}: {
  node: ReactFlowNode;
  onOpen: (id: string) => void;
  draggable: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  dimmed?: boolean;
  // Rendering in the blockers band rather than a cell: same card, red-tinted
  // surface so it reads as a hard stop wherever it sits.
  blocker?: boolean;
  // This is the sticky an open downgrade prompt is about. Neutral ring, not
  // a new hue — every colour channel on the board is already spoken for
  // (risk tints on cells, pine for drag-over, this card's own assumption
  // hue, destructive red for impossible marks) — so the ring has to read as
  // "this is the one" without borrowing any of them.
  ringed?: boolean;
}) {
  const data = node.data as { label?: string; metadata?: NodeMetadata } | undefined;
  const meta = data?.metadata;
  const blocking = isImpossibleBlocker(meta);
  const dismissedFlag = meta?.aiFlagged === 'impossible' && meta?.impossibleDismissed === true;

  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', node.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(node.id);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(node.id)}
      style={nodeTypeSurface(blocker ? 'hsl(var(--destructive))' : NODE_HUE.assumption)}
      className={cn(
        'relative w-34 rounded-md border p-2 text-left text-xs leading-snug shadow-xs transition-opacity',
        'hover:shadow-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
        draggable && 'cursor-grab active:cursor-grabbing',
        dimmed && 'opacity-40',
        // ring-foreground/25: a neutral tint, and :focus-visible's own higher
        // specificity (a pseudo-class beats a plain class) means the pine
        // focus-visible:ring-ring below still wins outright when both are
        // present at once, rather than the two ring colours blending.
        ringed && 'ring-2 ring-foreground/25',
      )}
    >
      {(blocking || dismissedFlag) && (
        <AlertTriangle
          className={cn(
            'absolute right-1 top-1 h-3 w-3',
            blocking ? 'text-destructive' : 'text-muted-foreground/50',
          )}
          aria-label={blocking ? 'Flagged impossible' : 'Impossible flag dismissed'}
        />
      )}
      <span className={cn('line-clamp-4 font-medium', (blocking || dismissedFlag) && 'pr-3')}>
        {data?.label || 'Untitled assumption'}
      </span>
    </button>
  );
}
